import { useRef, useEffect, useCallback } from 'react'
import { useGameStore } from '../store/gameStore'

interface GameCanvasProps {
  gameActive: boolean
  onGameOver: (victory: boolean) => void
}

export function GameCanvas({ gameActive, onGameOver }: GameCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  const initGame = useCallback(() => {
    if (!containerRef.current) return
    const container = containerRef.current

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    let W = window.innerWidth
    let H = window.innerHeight
    canvas.width = W; canvas.height = H
    canvas.style.cssText = 'width:100%;height:100%;display:block;'
    container.innerHTML = ''
    container.appendChild(canvas)

    // World
    const WORLD = 600
    const TILE = 40
    let camX = 0, camY = 0

    // Player
    const player = { x: 0, y: 0, r: 14, angle: 0, parachuting: true }

    // Bots
    const bots: {x:number; y:number; alive:boolean}[] = []
    for (let i = 0; i < 5; i++) bots.push({ x: (Math.random()-0.5)*200, y: (Math.random()-0.5)*200, alive: true })

    // Trees
    const trees: {x:number; y:number; r:number}[] = []
    for (let i = 0; i < 80; i++) trees.push({ x: (Math.random()-0.5)*WORLD, y: (Math.random()-0.5)*WORLD, r: 12+Math.random()*20 })

    // Input
    const keys: Record<string, boolean> = {}
    let mx = W/2, my = H/2
    window.addEventListener('keydown', e => keys[e.key.toLowerCase()] = true)
    window.addEventListener('keyup', e => keys[e.key.toLowerCase()] = false)
    window.addEventListener('mousemove', e => { mx = e.clientX; my = e.clientY })

    let frameId: number

    const draw = () => {
      frameId = requestAnimationFrame(draw)
      const state = useGameStore.getState()

      if (!state.isPlaying) {
        ctx.fillStyle = '#1a3a2a'
        ctx.fillRect(0, 0, W, H)
        ctx.fillStyle = '#00d4ff'
        ctx.font = 'bold 28px sans-serif'
        ctx.textAlign = 'center'
        ctx.fillText('PRESS PLAY TO START', W/2, H/2)
        return
      }

      const dt = 1/60
      const now = performance.now()

      // Parachute
      if (player.parachuting) {
        player.y -= 150*dt; player.x += Math.sin(now*0.001)*dt*30
        if (player.y <= 0) { player.y = 0; player.parachuting = false }
      }

      // Move
      if (!player.parachuting) {
        const speed = 140*dt
        let mvx = 0, mvy = 0
        if (keys['w']) mvy -= 1; if (keys['s']) mvy += 1
        if (keys['a']) mvx -= 1; if (keys['d']) mvx += 1
        const mlen = Math.sqrt(mvx*mvx + mvy*mvy)
        if (mlen > 0) { player.x += (mvx/mlen)*speed; player.y += (mvy/mlen)*speed }
      }

      // Camera
      camX += (player.x - camX)*0.1; camY += (player.y - camY)*0.1

      // Zone
      const zoneR = 350

      // DRAW WORLD
      ctx.fillStyle = '#4a9a5a'
      ctx.fillRect(0, 0, W, H)

      // Grid
      ctx.strokeStyle = 'rgba(0,0,0,0.08)'; ctx.lineWidth = 1
      for (let x = -(camX%TILE); x < W; x += TILE) { ctx.beginPath(); ctx.moveTo(x,0); ctx.lineTo(x,H); ctx.stroke() }
      for (let y = -(camY%TILE); y < H; y += TILE) { ctx.beginPath(); ctx.moveTo(0,y); ctx.lineTo(W,y); ctx.stroke() }

      // Trees - bright green with dark outline
      trees.forEach(t => {
        const sx = W/2 + (t.x - camX), sy = H/2 + (t.y - camY)
        ctx.fillStyle = '#1a5a1a'; ctx.beginPath(); ctx.arc(sx, sy, t.r, 0, Math.PI*2); ctx.fill()
        ctx.fillStyle = '#3add3a'; ctx.beginPath(); ctx.arc(sx, sy-t.r*0.3, t.r*0.7, 0, Math.PI*2); ctx.fill()
        ctx.strokeStyle = '#0a2a0a'; ctx.lineWidth = 2; ctx.beginPath(); ctx.arc(sx, sy, t.r, 0, Math.PI*2); ctx.stroke()
      })

      // Zone
      ctx.strokeStyle = 'rgba(0,255,136,0.5)'; ctx.setLineDash([10,6]); ctx.lineWidth = 4
      ctx.beginPath(); ctx.arc(W/2 - camX, H/2 - camY, zoneR, 0, Math.PI*2); ctx.stroke(); ctx.setLineDash([])

      // Bots - bright red triangles
      bots.forEach(b => {
        if (!b.alive) return
        const sx = W/2 + (b.x - camX), sy = H/2 + (b.y - camY)
        ctx.save(); ctx.translate(sx, sy); ctx.rotate(Math.atan2(player.y-b.y, player.x-b.x))
        ctx.fillStyle = '#ff0000'; ctx.beginPath(); ctx.moveTo(12,0); ctx.lineTo(-8,-8); ctx.lineTo(-5,0); ctx.lineTo(-8,8); ctx.closePath(); ctx.fill()
        ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke(); ctx.restore()
      })

      // Player - bright cyan with glow
      const psx = W/2 + (player.x - camX), psy = H/2 + (player.y - camY)
      ctx.save(); ctx.translate(psx, psy); ctx.rotate(Math.atan2(my - psy, mx - psx))
      ctx.fillStyle = '#00ffff'; ctx.shadowColor = '#00ffff'; ctx.shadowBlur = 25
      ctx.beginPath(); ctx.moveTo(18,0); ctx.lineTo(-10,-10); ctx.lineTo(-6,0); ctx.lineTo(-10,10); ctx.closePath(); ctx.fill()
      ctx.shadowBlur = 0; ctx.restore()

      // Parachute
      if (player.parachuting) {
        ctx.fillStyle = 'rgba(255,255,255,0.7)'; ctx.beginPath(); ctx.arc(psx, psy-25, 20, Math.PI, 0); ctx.fill()
      }

      // Minimap
      const mmS = 80, mmX = W - mmS - 10, mmY = H - mmS - 10
      ctx.fillStyle = 'rgba(0,0,0,0.5)'; ctx.fillRect(mmX, mmY, mmS, mmS)
      ctx.strokeStyle = 'rgba(255,255,255,0.2)'; ctx.strokeRect(mmX, mmY, mmS, mmS)
      const sc = mmS / WORLD
      ctx.strokeStyle = '#00ff88'; ctx.beginPath(); ctx.arc(mmX+mmS/2, mmY+mmS/2, zoneR*sc, 0, Math.PI*2); ctx.stroke()
      ctx.fillStyle = '#00d4ff'; ctx.beginPath(); ctx.arc(mmX+mmS/2+player.x*sc, mmY+mmS/2+player.y*sc, 3, 0, Math.PI*2); ctx.fill()
      bots.forEach(b => { if (b.alive) { ctx.fillStyle = '#ff0000'; ctx.fillRect(mmX+mmS/2+b.x*sc-1, mmY+mmS/2+b.y*sc-1, 3, 3) }})
    }

    draw()

    return () => { cancelAnimationFrame(frameId) }
  }, [])

  useEffect(() => { const cleanup = initGame(); return cleanup }, [initGame])
  useEffect(() => { if (gameActive) useGameStore.getState().startGame() }, [gameActive])

  return <div ref={containerRef} className="absolute inset-0 w-full h-full z-0" style={{background:'#2a5a3a'}} />
}
